// Iter 31.330 (05 Aug 2026) — Manage Principals admin surface.
//
// One-click UI for the principal binding + domain-alias system introduced
// in Iters 31.192 and 31.329. Lets Six attach `spyfinance.uk` (and any
// future domain) as an alias without curl-fu.
//
// Layout
// ======
// - Top: list of principals with member emails + domain-alias chips.
// - Per-principal: attach-domain input, remove-domain buttons, member list.
// - Bottom: "create new principal" is deliberately kept minimal — most
//   deployments only ever need one principal for Six.
import React, { useCallback, useEffect, useState } from "react";
import Shell from "@/components/Shell";
import { api } from "@/lib/api";
import { Loader2, Plus, X, UserPlus, AtSign } from "lucide-react";
import { toast } from "sonner";

function DomainChip({ domain, onRemove, disabled }) {
    return (
        <span
            data-testid={`domain-chip-${domain}`}
            className="inline-flex items-center gap-1 border border-ink/40 px-2 py-0.5 font-mono text-xs bg-parchment-soft"
        >
            <AtSign className="h-3 w-3 text-ink-soft" />
            {domain}
            <button
                onClick={() => onRemove(domain)}
                disabled={disabled}
                data-testid={`btn-remove-domain-${domain}`}
                className="ml-1 text-ink-soft hover:text-stamp-red disabled:opacity-30"
                title={`Detach @${domain}`}
            >
                <X className="h-3 w-3" />
            </button>
        </span>
    );
}

function MemberRow({ member, canonical }) {
    return (
        <div
            data-testid={`member-${member.id}`}
            className="flex items-center gap-2 text-xs py-1"
        >
            <span className="font-mono text-ink-soft">{member.id.slice(0, 8)}</span>
            <span className="font-mono">{member.email || "(no email)"}</span>
            <span className="text-ink-soft italic">{member.role}</span>
            {canonical === member.id && (
                <span
                    data-testid={`canonical-badge-${member.id}`}
                    className="border border-stamp-green/60 text-stamp-green px-1.5 py-0 font-heading uppercase text-[9px] tracking-[0.18em]"
                >
                    canonical
                </span>
            )}
        </div>
    );
}

function PrincipalCard({ principal, onDomainAdd, onDomainRemove, busy }) {
    const [newDomain, setNewDomain] = useState("");
    const submitDomain = async (e) => {
        e.preventDefault();
        if (!newDomain.trim()) return;
        await onDomainAdd(principal.id, newDomain.trim());
        setNewDomain("");
    };

    return (
        <div
            data-testid={`principal-${principal.id}`}
            className="border border-ink/25 p-5 space-y-4"
        >
            <div>
                <div className="flex items-baseline justify-between">
                    <h3
                        className="font-heading text-base tracking-[0.18em] uppercase"
                        data-testid={`principal-name-${principal.id}`}
                    >
                        {principal.display_name}
                    </h3>
                    <span className="font-mono text-[10px] text-ink-soft">
                        {principal.id.slice(0, 8)}…
                    </span>
                </div>
                <div className="mt-1 text-xs text-ink-soft">
                    {principal.members?.length || 0} member{principal.members?.length === 1 ? "" : "s"}
                    {" · "}
                    {(principal.email_domain_aliases?.length || 0)} domain alias{(principal.email_domain_aliases?.length || 0) === 1 ? "" : "es"}
                </div>
            </div>

            {/* Domain aliases */}
            <div>
                <div className="font-heading uppercase text-[10px] tracking-[0.2em] text-ink-soft mb-2">
                    domain aliases
                </div>
                <div className="flex flex-wrap gap-2 items-center">
                    {(principal.email_domain_aliases || []).map(d => (
                        <DomainChip
                            key={d}
                            domain={d}
                            onRemove={(d2) => onDomainRemove(principal.id, d2)}
                            disabled={busy}
                        />
                    ))}
                    {(!principal.email_domain_aliases || principal.email_domain_aliases.length === 0) && (
                        <span className="text-xs text-ink-soft italic">none — explicit members only</span>
                    )}
                </div>
                <form onSubmit={submitDomain} className="mt-3 flex items-center gap-2">
                    <input
                        type="text"
                        value={newDomain}
                        onChange={e => setNewDomain(e.target.value)}
                        placeholder="e.g. spyfinance.uk"
                        data-testid={`input-new-domain-${principal.id}`}
                        className="border border-ink/30 bg-parchment px-2 py-1 text-sm font-mono flex-1 max-w-xs"
                    />
                    <button
                        type="submit"
                        disabled={busy || !newDomain.trim()}
                        data-testid={`btn-add-domain-${principal.id}`}
                        className="border border-ink/40 hover:border-ink px-3 py-1 font-heading uppercase text-[10px] tracking-[0.2em] disabled:opacity-40"
                    >
                        <Plus className="h-3 w-3 inline mr-1" /> attach
                    </button>
                </form>
                <div className="mt-1 text-[11px] text-ink-soft italic">
                    Any user with an email ending @that.domain (case-insensitive) joins this principal&apos;s read scope automatically.
                </div>
            </div>

            {/* Members */}
            <div>
                <div className="font-heading uppercase text-[10px] tracking-[0.2em] text-ink-soft mb-2">
                    explicit members
                </div>
                <div data-testid={`members-list-${principal.id}`}>
                    {(principal.members || []).map(m => (
                        <MemberRow key={m.id} member={m} canonical={principal.canonical_user_id} />
                    ))}
                </div>
            </div>
        </div>
    );
}

export default function AdminPrincipals() {
    const [principals, setPrincipals] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/principals");
            setPrincipals(data.principals || []);
        } catch (err) {
            toast.error(`Load failed: ${err?.response?.data?.detail || err.message}`);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const handleDomainAdd = async (principalId, domain) => {
        setBusy(true);
        try {
            await api.post(`/admin/principals/${principalId}/domain`, { domain });
            toast.success(`@${domain} attached`);
            await load();
        } catch (err) {
            toast.error(`Attach failed: ${err?.response?.data?.detail || err.message}`);
        } finally {
            setBusy(false);
        }
    };

    const handleDomainRemove = async (principalId, domain) => {
        if (!window.confirm(`Detach @${domain} from this principal?`)) return;
        setBusy(true);
        try {
            await api.delete(`/admin/principals/${principalId}/domain/${encodeURIComponent(domain)}`);
            toast.success(`@${domain} detached`);
            await load();
        } catch (err) {
            toast.error(`Detach failed: ${err?.response?.data?.detail || err.message}`);
        } finally {
            setBusy(false);
        }
    };

    return (
        <Shell>
            <div className="space-y-6" data-testid="admin-principals-page">
                <header className="flex items-baseline justify-between border-b border-ink pb-4">
                    <div>
                        <h1 className="font-heading text-2xl tracking-[0.2em] uppercase" data-testid="admin-principals-title">
                            Manage Principals
                        </h1>
                        <p className="text-sm text-ink-soft mt-1">
                            Identity binding + domain-alias rules. Attach a domain to auto-adopt every current and future mailbox on it.
                        </p>
                    </div>
                    <button
                        onClick={load}
                        disabled={loading}
                        data-testid="btn-refresh"
                        className="border border-ink/30 hover:border-ink/60 px-3 py-2 font-heading uppercase text-[11px] tracking-[0.22em] disabled:opacity-40"
                    >
                        refresh
                    </button>
                </header>

                {loading && !principals.length && (
                    <div className="flex items-center gap-2 text-ink-soft" data-testid="loading-state">
                        <Loader2 className="h-4 w-4 animate-spin" /> loading…
                    </div>
                )}

                {!loading && principals.length === 0 && (
                    <div className="border border-dashed border-ink/25 p-6 text-center" data-testid="empty-state">
                        <UserPlus className="h-6 w-6 mx-auto text-ink-soft mb-2" />
                        <div className="font-heading uppercase text-sm tracking-[0.2em]">
                            No principals bound yet
                        </div>
                        <div className="text-xs text-ink-soft mt-2 italic">
                            Create one via <span className="font-mono">POST /api/admin/principals</span> with your canonical user_id and an initial member set. Once bound, you can attach domain aliases here.
                        </div>
                    </div>
                )}

                <div className="space-y-4" data-testid="principals-list">
                    {principals.map(p => (
                        <PrincipalCard
                            key={p.id}
                            principal={p}
                            onDomainAdd={handleDomainAdd}
                            onDomainRemove={handleDomainRemove}
                            busy={busy}
                        />
                    ))}
                </div>
            </div>
        </Shell>
    );
}
